import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { AdminAuthProvider } from './utils/AdminAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import api from './utils/api';
import { trackEvent, trackPageView } from './utils/analytics';
import {
  resolveAdminLoginUrl,
  isLandingHost,
  isPanelHost,
  resolveBookingOrigin,
  resolvePanelOrigin,
  resolvePanelRegisterUrl,
} from './utils/booking';
import { applySeo } from './utils/seo';

import BookingPage from './pages/BookingPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import OnboardingPage from './pages/OnboardingPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import RequestsPage from './pages/RequestsPage';
import CalendarPage from './pages/CalendarPage';
import ClientsPage from './pages/ClientsPage';
import ServicesPage from './pages/ServicesPage';
import IndustrySeoPage from './pages/IndustrySeoPage';
import { industryPages } from './data/industryPages';
import WhyFreePage from './pages/WhyFreePage';
import CityLandingPage from './pages/CityLandingPage';

import './App.css';

function ExternalRedirect({ to }) {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.location.replace(to);
    }
  }, [to]);

  return null;
}

function RootRoute() {
  const { isLoggedIn } = useAuth();

  if (isPanelHost()) {
    return <Navigate to={isLoggedIn ? '/panel' : '/login'} replace />;
  }

  return <LandingPage />;
}

function LoginRoute() {
  const location = useLocation();

  if (isLandingHost()) {
    return <ExternalRedirect to={`${resolvePanelOrigin()}${location.pathname}${location.search}`} />;
  }

  return <LoginPage />;
}

function RegisterRoute() {
  const location = useLocation();

  if (isLandingHost()) {
    return <ExternalRedirect to={`${resolvePanelRegisterUrl()}${location.search}`} />;
  }

  return <RegisterPage />;
}

function ForgotPasswordRoute() {
  const location = useLocation();

  if (isLandingHost()) {
    return <ExternalRedirect to={`${resolvePanelOrigin()}${location.pathname}${location.search}`} />;
  }

  return <ForgotPasswordPage />;
}

function ResetPasswordRoute() {
  const location = useLocation();

  if (isLandingHost()) {
    return <ExternalRedirect to={`${resolvePanelOrigin()}${location.pathname}${location.search}`} />;
  }

  return <ResetPasswordPage />;
}

function AdminLoginRoute() {
  const location = useLocation();

  if (isLandingHost()) {
    return <ExternalRedirect to={`${resolveAdminLoginUrl()}${location.search}`} />;
  }

  return <AdminLoginPage />;
}

function BookingRoute() {
  const location = useLocation();

  if (isPanelHost()) {
    return <ExternalRedirect to={`${resolveBookingOrigin()}${location.pathname}${location.search}`} />;
  }

  return <BookingPage />;
}

function PanelRoute({ children }) {
  const location = useLocation();

  if (isLandingHost()) {
    return <ExternalRedirect to={`${resolvePanelOrigin()}${location.pathname}${location.search}`} />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView({
      route_path: location.pathname,
      route_search: location.search || '',
    });

    if (isPanelHost() && (location.pathname === '/login' || location.pathname.startsWith('/panel') || location.pathname.startsWith('/admin'))) {
      trackEvent('panel_entry', {
        entry_path: location.pathname,
        entry_area: location.pathname === '/login' ? 'login' : (location.pathname.startsWith('/admin') ? 'admin' : 'panel'),
      });
    }
  }, [location.pathname, location.search]);

  return null;
}

function SeoRouteManager() {
  const location = useLocation();

  useEffect(() => {
    if (isPanelHost() && (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/forgot-password' || location.pathname === '/reset-password' || location.pathname.startsWith('/panel') || location.pathname.startsWith('/admin'))) {
      const title = location.pathname.startsWith('/admin')
        ? 'Admin ClientWay'
        : location.pathname === '/login'
          ? 'Logowanie | ClientWay'
          : location.pathname === '/register'
            ? 'Rejestracja | ClientWay'
            : location.pathname === '/forgot-password'
              ? 'Reset hasła | ClientWay'
              : location.pathname === '/reset-password'
                ? 'Ustaw nowe hasło | ClientWay'
                : 'Panel ClientWay';
      const description = location.pathname.startsWith('/admin')
        ? 'Panel administracyjny ClientWay.'
        : 'Panel ClientWay dla usługodawców.';

      applySeo({
        title,
        description,
        robots: 'noindex,nofollow,noarchive,nosnippet',
        canonical: `${resolvePanelOrigin()}${location.pathname}${location.search}`,
        ogTitle: title,
        ogDescription: description,
        ogUrl: `${resolvePanelOrigin()}${location.pathname}${location.search}`,
        themeColor: '#fffaf6',
        structuredData: null,
      });
    }
  }, [location.pathname, location.search]);

  return null;
}

export default function App() {
  useEffect(() => {
    let isReporting = false;

    function reportClientError(payload) {
      const token = localStorage.getItem('bf_token');
      if (!token || isReporting) {
        return;
      }

      isReporting = true;
      fetch(`${api.defaults.baseURL}/logs/client-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...payload,
          userAgent: window.navigator.userAgent,
          path: window.location.pathname,
        }),
      }).finally(() => {
        isReporting = false;
      });
    }

    function handleWindowError(event) {
      reportClientError({
        message: event.message,
        source: event.filename,
        stack: event.error?.stack || null,
      });
    }

    function handleUnhandledRejection(event) {
      reportClientError({
        message: event.reason?.message || 'Unhandled promise rejection',
        source: 'promise',
        stack: event.reason?.stack || null,
      });
    }

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <SeoRouteManager />
          <Routes>
          {/* Publiczne */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/book/:professionalKey" element={<BookingRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/register" element={<RegisterRoute />} />
          <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
          <Route path="/reset-password" element={<ResetPasswordRoute />} />
          <Route path="/admin/login" element={<AdminLoginRoute />} />
          <Route path="/fryzjer" element={<IndustrySeoPage slug={industryPages.fryzjer.slug} />} />
          <Route path="/kosmetyczka" element={<IndustrySeoPage slug={industryPages.kosmetyczka.slug} />} />
          <Route path="/barber" element={<IndustrySeoPage slug={industryPages.barber.slug} />} />
          <Route path="/fizjoterapeuta" element={<IndustrySeoPage slug={industryPages.fizjoterapeuta.slug} />} />
          <Route path="/trener-personalny" element={<IndustrySeoPage slug={industryPages['trener-personalny'].slug} />} />
          <Route path="/dlaczego-za-darmo" element={<WhyFreePage />} />
          <Route path="/rezerwacje/:city" element={<CityLandingPage />} />

          {/* Chronione – panel kosmetyczki */}
          <Route
            path="/panel"
            element={<PanelRoute><DashboardPage /></PanelRoute>}
          />
          <Route
            path="/panel/onboarding"
            element={<PanelRoute><OnboardingPage /></PanelRoute>}
          />
          <Route
            path="/panel/requests"
            element={<PanelRoute><RequestsPage /></PanelRoute>}
          />
          <Route
            path="/panel/calendar"
            element={<PanelRoute><CalendarPage /></PanelRoute>}
          />
          <Route
            path="/panel/clients"
            element={<PanelRoute><ClientsPage /></PanelRoute>}
          />
          <Route
            path="/panel/services"
            element={<PanelRoute><ServicesPage /></PanelRoute>}
          />

          <Route
            path="/admin"
            element={<ProtectedAdminRoute><AdminDashboardPage /></ProtectedAdminRoute>}
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
